import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getVerticalLabels, type VerticalLabels } from "@/lib/vertical";
import { verticalLocalePrefix } from "@/lib/i18n/vertical-locale-prefix";

/** Same shape as `getVerticalLabels`, with optional per-locale overrides under `vertical.{fitness|str}.*`. */
export function useTranslatedVerticalLabels(vertical: unknown): VerticalLabels {
  const { t } = useTranslation();
  const raw = getVerticalLabels(vertical);
  const prefix = verticalLocalePrefix(vertical);
  return useMemo(() => {
    const tv = (key: keyof VerticalLabels) =>
      t(`vertical.${prefix}.${String(key)}`, { defaultValue: raw[key] });
    return {
      dataSectionNavLabel: tv("dataSectionNavLabel"),
      dataSectionPageTitle: tv("dataSectionPageTitle"),
      dataSectionReadOnlyNoun: tv("dataSectionReadOnlyNoun"),
      dataSectionAddUnitExpandedLabel: tv("dataSectionAddUnitExpandedLabel"),
      dataSectionListColumnTitle: tv("dataSectionListColumnTitle"),
      dataSectionMobileDescription: tv("dataSectionMobileDescription"),
      aggregateScopeLabel: tv("aggregateScopeLabel"),
      selectScopeActionLabel: tv("selectScopeActionLabel"),
      overlaySelectScopeMessage: tv("overlaySelectScopeMessage"),
      teamInviteUnitsMulti: tv("teamInviteUnitsMulti"),
      teamInviteUnitsSingle: tv("teamInviteUnitsSingle"),
      teamInviteNoUnitsAvailable: tv("teamInviteNoUnitsAvailable"),
      teamInviteSelectAtLeastOne: tv("teamInviteSelectAtLeastOne"),
      chatsConversationUnitLabel: tv("chatsConversationUnitLabel"),
      chatsIdentificationMatchingCopy: tv("chatsIdentificationMatchingCopy"),
      agentTraceSelectScopeMessage: tv("agentTraceSelectScopeMessage"),
      agentTraceLoadErrorNeedsScope: tv("agentTraceLoadErrorNeedsScope"),
      agentTraceNoAuditEventsForUnit: tv("agentTraceNoAuditEventsForUnit"),
      aiSettingsContactOwnerReadOnly: tv("aiSettingsContactOwnerReadOnly"),
      onboardingBookingConnectTitle: tv("onboardingBookingConnectTitle"),
      onboardingBookingConnectSubtitle: tv("onboardingBookingConnectSubtitle"),
      onboardingBookingUsernamePlaceholder: tv("onboardingBookingUsernamePlaceholder"),
      dashboardStatConversationsToday: tv("dashboardStatConversationsToday"),
      dashboardStatHandledByAiPct: tv("dashboardStatHandledByAiPct"),
      dashboardStatBookingsMade: tv("dashboardStatBookingsMade"),
      dashboardStatAvgFirstResponse: tv("dashboardStatAvgFirstResponse"),
      chatsMockJoinedBookingLine: tv("chatsMockJoinedBookingLine"),
      chatsMockMembershipHeading: tv("chatsMockMembershipHeading"),
      chatsMockScheduleHeading: tv("chatsMockScheduleHeading"),
      chatsCustomerPanelPlatformsHeading: tv("chatsCustomerPanelPlatformsHeading"),
      chatsCustomerPanelAggregatorLabel: tv("chatsCustomerPanelAggregatorLabel"),
      chatsCustomerPanelNetworkLabel: tv("chatsCustomerPanelNetworkLabel"),
      chatsCustomerPanelPrimaryPlatformIdLabel: tv("chatsCustomerPanelPrimaryPlatformIdLabel"),
      chatsCustomerPanelSecondaryPlatformIdLabel: tv("chatsCustomerPanelSecondaryPlatformIdLabel"),
      chatsCustomerPanelAggregatorIdLabel: tv("chatsCustomerPanelAggregatorIdLabel"),
      chatsCustomerPanelCustomerRecordIdLabel: tv("chatsCustomerPanelCustomerRecordIdLabel"),
      chatsCustomerPanelConversationRefLabel: tv("chatsCustomerPanelConversationRefLabel"),
      chatsUnmatchedNotFoundTitle: tv("chatsUnmatchedNotFoundTitle"),
      chatsUnmatchedNotFoundBody: tv("chatsUnmatchedNotFoundBody"),
      chatsIdentificationInProgressTitle: tv("chatsIdentificationInProgressTitle"),
      chatsNoMembershipRecordTitle: tv("chatsNoMembershipRecordTitle"),
      chatsNoMembershipRecordBody: tv("chatsNoMembershipRecordBody"),
      visionAgentTraceButtonLabel: tv("visionAgentTraceButtonLabel"),
      pageScopeDescription: tv("pageScopeDescription"),
      connectionsTableUnitColumn: tv("connectionsTableUnitColumn"),
      settingsAccountVerticalTitle: tv("settingsAccountVerticalTitle"),
      settingsAccountVerticalHint: tv("settingsAccountVerticalHint"),
    };
  }, [t, prefix, raw]);
}
